import { datadogRum } from '@datadog/browser-rum';
import type { PostProcessPopupParams } from 'ag-grid-community';

export const AG_GRID_MENU_CLICK_ACTION_PREFIX = 'Click on Context Menu Item';

// AG Grid does not expose a generic "menu item clicked" event. We attach to the rendered popup instead,
// and use a WeakSet so repeated postProcessPopup calls for the same DOM node do not add duplicate listeners.
const trackedPopups = new WeakSet<HTMLElement>();

// A click can bubble through nested menu popups. Mark the event once it has been tracked so submenus do not
// produce duplicate Datadog actions for a single user click.
const TRACKED_AG_GRID_MENU_CLICK = Symbol('TRACKED_AG_GRID_MENU_CLICK');
type TrackedMouseEvent = MouseEvent & { [TRACKED_AG_GRID_MENU_CLICK]?: true };

/**
 * Tracks AG Grid context-menu item clicks from the popup DOM.
 *
 * We intentionally track rendered menu options instead of wrapping MenuItemDef.action callbacks because AG Grid's
 * built-in menu items are often provided as strings and their actions are owned internally by AG Grid.
 */
export function trackAgGridContextMenuPopupClicks(params: PostProcessPopupParams): void {
  if (params.type !== 'contextMenu' && params.type !== 'subMenu') {
    return;
  }

  if (trackedPopups.has(params.ePopup)) {
    return;
  }
  trackedPopups.add(params.ePopup);

  params.ePopup.addEventListener('click', event => {
    const trackedEvent = event as TrackedMouseEvent;
    if (trackedEvent[TRACKED_AG_GRID_MENU_CLICK]) {
      return;
    }

    const menuOption = getAgGridMenuOption(event.target, params.ePopup);
    if (menuOption == null || menuOption.classList.contains('ag-disabled')) {
      return;
    }

    const itemName = getAgGridMenuItemName(menuOption);
    if (!itemName) {
      return;
    }

    trackedEvent[TRACKED_AG_GRID_MENU_CLICK] = true;
    const colId = params.column?.getColId();
    datadogRum.addAction(`${AG_GRID_MENU_CLICK_ACTION_PREFIX}: ${itemName}`, {
      colId,
      rowID: params.rowNode?.id,
      text: itemName,
    });
  });
}

function getAgGridMenuOption(target: EventTarget | null, popup: HTMLElement): HTMLElement | null {
  if (!(target instanceof Element)) {
    return null;
  }

  // AG Grid currently renders menu entries with .ag-menu-option, but [role="menuitem"] keeps this resilient to
  // markup changes and custom menu item components that preserve the accessibility role.
  const menuOption = target.closest<HTMLElement>('.ag-menu-option, [role="menuitem"]');
  return menuOption != null && popup.contains(menuOption) ? menuOption : null;
}

function getAgGridMenuItemName(menuOption: HTMLElement): string {
  // Prefer the text node AG Grid uses for the label so icon text / nested markup does not leak into Datadog.
  const itemNameElement = menuOption.querySelector<HTMLElement>('.ag-menu-option-text, [ref="eName"]');
  return (
    itemNameElement?.innerText ??
    itemNameElement?.textContent ??
    menuOption.innerText ??
    menuOption.textContent ??
    ''
  )
    .replace(/\s+/g, ' ')
    .trim();
}
